"use client"

import { getBlurSVGPlaceholder } from "@/app/(site)/_components/Atoms/blurry-placeholder"
import { getAccessibleImageAlt } from "@/src/utils/a11y/getAccessibleImageAlt"
import {
    bunnyLoader,
    checkIfImageSrcIsCentraImage,
    defaultMediaLoaderProps,
    isSanityImageObject,
    type MediaLoaderProps,
    sanityBaseUrl
} from "@/lib/sanityLoader"
import placeholder from "@assets/images/placeholder.png"
import Image, { type ImageProps } from "next/image"
import { useRef, useState } from "react"

interface BaseImageProps extends Omit<ImageProps, "src"> {
    src: string
    mimeType?: string
    objectFit?: string
    objectPosition?: string
    isAboveTheFold?: boolean
    sizes?: string
    width?: number
    height?: number
    asset?: {
        metadata?: {
            lqip?: string
        }
        width?: number
        height?: number
    }
    isPdp?: boolean
    unoptimized?: boolean
    mediaLoaderProps?: Omit<MediaLoaderProps, "src" | "width">
}

export default function BaseImage({
    mimeType,
    src,
    isAboveTheFold,
    sizes = "80vw",
    width,
    height,
    asset,
    isPdp = false,
    unoptimized = false, // sets in next.config.js
    mediaLoaderProps = {
        ...defaultMediaLoaderProps
    },
    ...props
}: BaseImageProps) {
    const imageRef = useRef<HTMLImageElement>(null)
    const [defaultSrc, setSrc] = useState(src)

    const isSanityImage =
        (typeof defaultSrc === "string" && defaultSrc.startsWith(sanityBaseUrl)) ||
        (typeof defaultSrc === "string" && defaultSrc.startsWith("image-")) ||
        isSanityImageObject(defaultSrc)
    const isCentraImage =
        !isSanityImage &&
        defaultSrc &&
        typeof defaultSrc === "string" &&
        checkIfImageSrcIsCentraImage(defaultSrc)

    const blurUrl = getBlurSVGPlaceholder(
        asset?.metadata?.lqip || "",
        asset?.width || 1280,
        asset?.height || 853
    )

    // Handle image load to remove blurry placeholder
    const handleRemoveBlurPlaceholder = () => {
        if (imageRef.current) {
            const currentBackground = imageRef.current.style.backgroundImage
            if (currentBackground && currentBackground !== "none") {
                imageRef.current.style.backgroundImage = "none"
            }
        }
    }

    const customLoader = isCentraImage
        ? (loaderProps: { src: string; width: number; quality?: number }) =>
              bunnyLoader({ ...loaderProps, ...mediaLoaderProps })
        : undefined

    // Style with blurry placeholder as background (appears immediately when CSS is parsed)
    // The real image will naturally overlay the blurry placeholder when it loads
    const imageStyle = {
        ...(blurUrl &&
            isSanityImage &&
            asset?.metadata?.lqip && {
                backgroundImage: `url("${blurUrl}")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat"
            })
    }

    // Determine if we need to use fill or explicit dimensions
    const useFill = props.fill !== undefined
    const imageProps = useFill ? { fill: true } : { width, height }

    // Determine if we should use unoptimized mode
    // Use unoptimized for static assets and other non-Sanity/non-Centra images
    const shouldUseUnoptimized = unoptimized || (!isSanityImage && !isCentraImage)

    return (
        <Image
            {...props}
            {...imageProps}
            ref={imageRef}
            onLoad={blurUrl && isSanityImage ? handleRemoveBlurPlaceholder : undefined}
            unoptimized={shouldUseUnoptimized}
            onError={() => setSrc(placeholder.src)} // Fallback to placeholder on error
            alt={getAccessibleImageAlt(typeof props.alt === "string" ? props.alt : "")}
            src={
                typeof defaultSrc === "string" && defaultSrc.length > 0
                    ? defaultSrc
                    : placeholder.src
            } // Use asset ref for src
            loader={customLoader}
            sizes={sizes}
            preload={isAboveTheFold ? true : undefined}
            loading={isAboveTheFold ? "eager" : undefined}
            style={{
                ...imageStyle,
                ...props.style
            }}
            className={props.className || ""}
        />
    )
}
